import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon, ButtonIconPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import TabsDataHandler from "@sunSource/Store/Data/TabsData/Handler/TabsDataHandler";
import TabTagSearchQueryRemoveHandler from "@sunSource/Store/Data/TabsData/Handler/TabTagSearchQueryRemoveHandler";
import TabsSearchTermHandler from "@sunSource/Store/Data/TabsData/Handler/TabsSearchTermHandler";

import React, { useState, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";

/* ============================
   Redux Mapping
============================ */

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });

    return {
        pageTitle: getCurrentPage(state).fields.title,
        termsearch: state.sunSourceState.TabsActiveData.tabsSearchTerm,
        activeTabName: state.sunSourceState.TabsActiveData.activeTab,
        pages: pages.value || [],
        tagsName: state.sunSourceState.TabsActiveData.tagsName,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    TabsSearchTermHandler,
    TabTagSearchQueryRemoveHandler,
    TabsDataHandler,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

/* ============================
   Styles
============================ */

export interface TabSearchBarStyles {
    wrapper?: InjectableCss;
    input?: TextFieldPresentationProps;
    buttonIcon?: ButtonIconPresentationProps;
    tagsText?: InjectableCss;
    closeIcon?: IconPresentationProps;
    inputWrapper?: InjectableCss;
    tagWrapper?: InjectableCss;
}

export const tabSearchBarStyles: TabSearchBarStyles = {
    wrapper: {
        css: css`
            margin: 30px 0px 20px;
        `,
    },

    inputWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 20px;
        `,
    },

    input: {
        sizeVariant: "default",
        iconProps: {
            src: Search,
            css: css`
                width: 45px;
                left: 0px;
                border-radius: 0px;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 45px !important;
                width: 45px !important;
                > svg {
                    stroke: #878787;
                    height: 22px !important;
                    width: 22px !important;
                }
            `,
        },
        backgroundColor: "transparent",
        cssOverrides: {
            formField: css`
                width: 100%;
                ${StyledButton} {
                    left: 0;
                    right: auto;
                    & svg {
                        stroke: #878787;
                    }
                }
            `,
        },
        css: css`
            border: 0 !important;
            padding: 0 10px 0 50px !important;
            border-radius: 3px !important;
            height: 46px !important;
            background: #e7e6ec !important;
        `,
    },

    buttonIcon: {
        css: css`
            padding: 20px;
            height: 100%;
            display: block;
            position: relative;
            border-radius: 3px;
            background-color: #000;
            color: #fff;
            cursor: pointer;
            height: 45px;
            width: 65px;
            > svg {
                position: absolute;
                left: 35%;
                top: 27%;
                width: 20px;
                height: 20px;
                transform: translate(0%, 0%);
            }
        `,
    },

    tagsText: {
        css: css`
            background: #00539b;
            padding: 6px 20px;
            border-radius: 41px;
            width: fit-content;
            text-align: left;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            color: #fff;
            text-transform: capitalize;
        `,
    },

    closeIcon: {
        css: css`
            width: 16px;
            height: 16px;
            margin-left: 10px;
            cursor: pointer;
            > svg {
                width: 16px;
                height: 16px;
                stroke: #fff;
            }
        `,
    },

    tagWrapper: {
        css: css`
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            margin-top: 15px;
        `,
    },
};

const styles = tabSearchBarStyles;

/* ============================
   Component
============================ */

const TabsSearchBar: React.FC<Props> = props => {
    const {
        TabsSearchTermHandler,
        TabTagSearchQueryRemoveHandler,
        TabsDataHandler,
        termsearch,
        activeTabName,
        pages,
        tagsName,
        history,
    } = props;

    const [searchTerm, setSearchTerm] = useState("");

    /* ============================
       Load Filters from URL
    ============================ */

    useEffect(() => {
        if (!pages.length) {
            return;
        }

        const url = new URL(window.location.href);

        const searchValue = url.searchParams.get("search") ?? "";
        const tabValue = url.searchParams.get("tab") ?? "";
        const tagValue = url.searchParams.get("tag") ?? "";

        TabsDataHandler({ activeTab: tabValue });

        if (searchValue !== "") {
            setSearchTerm("");
            TabsSearchTermHandler({ tabsSearchTerm: searchValue });
        } else {
            TabTagSearchQueryRemoveHandler({ activeTab: tabValue, tabsSearchTerm: "" });
        }

        if (tagValue !== "") {
            TabsSearchTermHandler({ tagsName: tagValue });
        } else {
            TabTagSearchQueryRemoveHandler({ activeTab: tabValue, tagsName: "" });
        }
    }, [pages, props.location.search]);

    /* ============================
       Search
    ============================ */

    const doSearch = () => {
        const trimmedSearchTerm = searchTerm.trim();

        if (!trimmedSearchTerm) return;

        TabsSearchTermHandler({ tabsSearchTerm: trimmedSearchTerm });

        const url = new URL(window.location.href);
        url.searchParams.set("search", trimmedSearchTerm);
        url.searchParams.set("page", "1");

        // window.history.replaceState({}, "", url.toString());
        history.replace(url.pathname + url.search);
        setSearchTerm("")
    };

    /* ============================
       Remove Search
    ============================ */

    const removeSearch = () => {
        setSearchTerm("");

        // TabsDataHandler({ activeTab: activeTabName });
        TabTagSearchQueryRemoveHandler({ activeTab: activeTabName, tabsSearchTerm: "" });

        const url = new URL(window.location.href);
        url.searchParams.delete("search");
        url.searchParams.set("page", "1");

        history.replace(url.pathname + url.search);
    };

    /* ============================
       Remove Tag
    ============================ */

    const removeTag = (tagName: string) => {
        // TabsDataHandler({ activeTab: activeTabName });
        TabTagSearchQueryRemoveHandler({ activeTab: activeTabName, tagsName: tagName });

        const url = new URL(window.location.href);

        const tagParam = url.searchParams.get("tag");

        if (!tagParam) return;

        const normalizedTag = tagName.trim().toLowerCase();

        const tags = tagParam.split(",").map(tag => tag.trim());

        const updatedTags = tags.filter(tag => tag.toLowerCase() !== normalizedTag);

        if (updatedTags.length > 0) {
            url.searchParams.set("tag", updatedTags.join(","));
        } else {
            url.searchParams.delete("tag");
        }
        url.searchParams.set("page", "1");

        history.replace(url.pathname + url.search);
    };
    /* ============================
       Render
    ============================ */

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.inputWrapper}>
                <TextField
                    {...styles.input}
                    placeholder="Search All Content"
                    value={searchTerm}
                    autoComplete="off"
                    onChange={(e:any) => setSearchTerm(e.target.value)}
                    onKeyDown={(e: any) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            doSearch();
                        }
                    }}
                />

                <ButtonIcon {...styles.buttonIcon} src={Search} onClick={doSearch} />
            </StyledWrapper>

            <StyledWrapper {...styles.tagWrapper}>
                {termsearch && (
                    <StyledWrapper {...styles.tagsText}>
                        {termsearch}
                        <Icon src={X} {...styles.closeIcon} onClick={removeSearch} />
                    </StyledWrapper>
                )}

                {Array.isArray(tagsName) &&
                    tagsName.map(tag => (
                        <StyledWrapper key={tag} {...styles.tagsText}>
                            {tag}
                            <Icon src={X} {...styles.closeIcon} onClick={() => removeTag(tag)} />
                        </StyledWrapper>
                    ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

/* ============================
   Widget Export
============================ */

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(TabsSearchBar)),

    definition: {
        displayName: "Tabs Search Bar",
        group: "Common",
    },
};

export default widgetModule;
